import { type ConditionalOperator, findConditionRuleProblem, pickT } from "@repo/shared";
import type { CustomFieldOption, FlowField } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError, patch } from "@/lib/api/_client";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import { log } from "@/lib/logger/browser-logger";
import type { FlowPageLayout, LayoutConfig, PageType } from "@/lib/types/flow";
import { ConditionalVisibilityFields } from "./conditional-visibility-fields";
import { T } from "./layout-settings-dialog.i18n";

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  options?: CustomFieldOption[] | null;
  isActive: boolean;
};

type LayoutSettingsDialogProps = {
  open: boolean;
  flowId: string;
  flowLanguage: string;
  layout: FlowPageLayout | null;
  availableFields: FlowField[];
  clientCustomFields?: ClientCustomField[];
  onClose: (success: boolean) => void;
  pageType?: PageType;
  /** QL-120 — enables the goal-type source on goal/portfolio pages. */
  portfolioCollectionId?: string | null;
};

export function LayoutSettingsDialog({
  open,
  flowId,
  flowLanguage,
  layout,
  availableFields,
  clientCustomFields = [],
  onClose,
  pageType = "global",
  portfolioCollectionId = null,
}: LayoutSettingsDialogProps) {
  const t = pickT(T, flowLanguage);

  // QL-13 — the flow's quizzes, so a rule naming a deleted one is caught on
  // save rather than silently hiding content at runtime. While the list is
  // loading it is empty, which would read as "every quiz is gone", so the
  // check waits for it.
  const { quizzes: flowQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Conditional display state
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [operator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  useEffect(() => {
    if (open && layout) {
      // Parse existing config
      try {
        const config: LayoutConfig = layout.config ? JSON.parse(layout.config) : {};

        // Load conditional display settings
        if (config.conditionalDisplay) {
          setConditionalEnabled(config.conditionalDisplay.enabled || false);
          setSourceFieldId(config.conditionalDisplay.sourceFieldId || "");
          setOperator(config.conditionalDisplay.operator || "equals");
          setConditionValue(config.conditionalDisplay.value || "");
        } else {
          setConditionalEnabled(false);
          setSourceFieldId("");
          setOperator("equals");
          setConditionValue("");
        }
      } catch (err) {
        log.error({ err }, "failed to parse flow layout config");
        setConditionalEnabled(false);
        setSourceFieldId("");
        setOperator("equals");
        setConditionValue("");
      }
    } else {
      // Reset when closing
      setConditionalEnabled(false);
      setSourceFieldId("");
      setOperator("equals");
      setConditionValue("");
    }
    setError(null);
  }, [layout, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!layout) return;

    setLoading(true);
    setError(null);

    try {
      // Parse existing config to preserve other settings (tabs, columns, …)
      let existingConfig: LayoutConfig = {};
      try {
        existingConfig = layout.config ? JSON.parse(layout.config) : {};
      } catch {
        existingConfig = {};
      }

      // Build config object on top of what is already stored
      const config: LayoutConfig = { ...existingConfig };

      // Add conditional display if enabled
      if (conditionalEnabled) {
        const ruleProblem = findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : flowQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        );
        if (ruleProblem) {
          setError(conditionRuleProblemMessage(ruleProblem, flowLanguage));
          setLoading(false);
          return;
        }

        config.conditionalDisplay = {
          enabled: true,
          sourceFieldId,
          operator,
          value:
            operator === "is_empty" || operator === "is_not_empty" ? undefined : conditionValue,
        };
      } else {
        config.conditionalDisplay = {
          enabled: false,
          sourceFieldId: "",
          operator: "equals",
        };
      }

      await patch(`/internal/flows/${flowId}/layouts/${layout.id}`, {
        config: JSON.stringify(config),
      });

      onClose(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.failedToUpdate);
    } finally {
      setLoading(false);
    }
  };

  // Determine the title based on layout type
  const layoutTypeLabel = layout?.layoutType === "tabs_block" ? t.tabsBlock : t.twoColumnBlock;

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {layoutTypeLabel} {t.settings}
          </DialogTitle>
          <DialogDescription>{t.configureConditional}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Conditional Display */}
            <ConditionalVisibilityFields
              enabled={conditionalEnabled}
              onEnabledChange={setConditionalEnabled}
              sourceFieldId={sourceFieldId}
              onSourceFieldIdChange={setSourceFieldId}
              operator={operator}
              onOperatorChange={setOperator}
              value={conditionValue}
              onValueChange={setConditionValue}
              availableFields={availableFields}
              clientCustomFields={clientCustomFields}
              disabled={loading}
              flowLanguage={flowLanguage}
              description={t.showBlockCondition}
              pageType={pageType}
              portfolioCollectionId={portfolioCollectionId}
              flowId={flowId}
            />
          </div>

          <DialogFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
